import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './BuildingSelectionMap.scss';
import { Trans } from 'react-i18next';

interface BuildingSelectionMapProps {
  location: { lat: number; lng: number };
  onBuildingSelect: (buildingId: string | null, buildingGeometry: any | null, userClickedCoordinates: { lat: number; lng: number } | null) => void;
  isVisible: boolean;
  isQueryingBuilding: boolean;
  address: string;
  buildingId: string | null;
  buildingGeometry?: any;
  showBuildingSelectionMap: boolean;
  isAddressIncomplete: boolean;
  addressMismatchError: boolean;
  actionType: string;
  buildingAddress: string;
}

const BuildingSelectionMap: React.FC<BuildingSelectionMapProps> = ({
  location,
  onBuildingSelect,
  isVisible,
  isQueryingBuilding,
  address,
  buildingId,
  buildingGeometry,
  showBuildingSelectionMap,
  isAddressIncomplete,
  addressMismatchError,
  actionType,
  buildingAddress
}) => {
  // Keep a stable ref to the callback to avoid unnecessary effect reruns
  const onBuildingSelectRef = useRef(onBuildingSelect);
  onBuildingSelectRef.current = onBuildingSelect;
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null);
  const [hoveredBuildingId, setHoveredBuildingId] = useState<string | null>(null);

  // Resize the map defensively when UI content around it changes
  useEffect(() => {
    if (mapRef.current) {
      try { mapRef.current.resize(); } catch { /* no-op */ }
    }
  }, [selectedBuildingId, buildingAddress]);

  // Sync the buildingId prop with internal selectedBuildingId state
  // This handles cases where the building is pre-selected (e.g., after returning from payment flow)
  useEffect(() => {
    // Only sync if we have a map, the prop differs from state, and we have geometry
    if (mapRef.current && buildingId && buildingId !== selectedBuildingId && buildingGeometry) {
      setSelectedBuildingId(buildingId);
      
      // Update the selected building highlight on the map
      const source = mapRef.current.getSource('selected-building') as mapboxgl.GeoJSONSource;
      if (source) {
        try {
          source.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: buildingGeometry,
              properties: {}
            }]
          });
          // Ensure canvas adjusts after DOM changes
          mapRef.current.resize();
        } catch (err) {
          console.error('Error setting pre-selected building on map', err);
        }
      }
    } else if (mapRef.current && !buildingId && selectedBuildingId) {
      // Clear selection if buildingId becomes null
      setSelectedBuildingId(null);
      const source = mapRef.current.getSource('selected-building') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: []
        });
      }
    }
  }, [buildingId, buildingGeometry, selectedBuildingId]);

  // Initialise the map once when the component becomes visible.
  useEffect(() => {
    if (!isVisible || mapRef.current) return; // already created or not visible
    if (!mapContainerRef.current || !location) return;

    // Initialize map
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
      center: [location.lng, location.lat],
      zoom: 17, // High zoom to see buildings clearly
    });

    // Add navigation controls
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Create a custom marker element for the address location
    const markerElement = document.createElement('div');
    markerElement.className = 'building-selection-marker';
    markerElement.innerHTML = `
      <svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24c0-6.627-5.373-12-12-12zm0 16c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4z" fill="#FF5A5F"/>
      </svg>
    `;

    // Add marker to map
    markerRef.current = new mapboxgl.Marker(markerElement, {
      offset: [0, -18]
    })
      .setLngLat([location.lng, location.lat])
      .addTo(mapRef.current);

    // Wait for map to load
    mapRef.current.on('load', () => {
      if (!mapRef.current) return;

      // Add a source for highlighting selected building (avoid duplicate add)
      if (!mapRef.current.getSource('selected-building')) {
        mapRef.current.addSource('selected-building', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
      } else {
        try {
          (mapRef.current.getSource('selected-building') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] });
        } catch {}
      }

      // Add a layer for the selected building highlight
      if (!mapRef.current.getLayer('selected-building-fill')) {
        mapRef.current.addLayer({
          id: 'selected-building-fill',
          type: 'fill',
          source: 'selected-building',
          paint: {
            'fill-color': '#8095FF',
            'fill-opacity': 0.4
          }
        });
      }

      // Add a layer for the selected building outline
      if (!mapRef.current.getLayer('selected-building-outline')) {
        mapRef.current.addLayer({
          id: 'selected-building-outline',
          type: 'line',
          source: 'selected-building',
          paint: {
            'line-color': '#8095FF',
            'line-width': 3
          }
        });
      }

      // Change cursor on hover over buildings
      mapRef.current.on('mouseenter', 'building', () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = 'pointer';
        }
      });

      mapRef.current.on('mouseleave', 'building', () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = '';
        }
        setHoveredBuildingId(null);
      });

      // Handle hover effect
      mapRef.current.on('mousemove', 'building', (e) => {
        if (e.features && e.features.length > 0) {
          const buildingId = e.features[0].id?.toString() || null;
          setHoveredBuildingId(buildingId);
        }
      });

      // Handle building click
      mapRef.current.on('click', 'building', (e) => {
        if (!e.features || e.features.length === 0) return;
        const feature = e.features[0];
        const buildingId = feature.id?.toString() || null;
        const userClickedCoordinates = { lat: e.lngLat.lat, lng: e.lngLat.lng };

        // Attempt to gather **all** slices of the same building (same feature id) currently rendered
        let mergedCoordinates: number[][][] = [];
        if (mapRef.current && buildingId) {
          try {
            const slices = mapRef.current.queryRenderedFeatures({
              layers: ['building'],
              filter: ['==', ['id'], feature.id as any]
            });

            slices.forEach((slice) => {
              if (slice.geometry?.type === 'Polygon') {
                mergedCoordinates.push(...(slice.geometry.coordinates as number[][][]));
              } else if (slice.geometry?.type === 'MultiPolygon') {
                // flatten
                (slice.geometry.coordinates as number[][][][]).forEach((poly) => {
                  mergedCoordinates.push(...poly);
                });
              }
            });
          } catch (err) {
            console.warn('Could not queryRenderedFeatures for building slices', err);
          }
        }

        // Fallback to original feature geometry if we did not manage to merge
        if (mergedCoordinates.length === 0 && feature.geometry?.type === 'Polygon') {
          mergedCoordinates = feature.geometry.coordinates as number[][][];
        }

        // Use polygon-clipping to union slices and remove internal borders
        let buildingGeometry: any;
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const pc = require('polygon-clipping');
          const unionResult: number[][][][] = pc.union(
            ...mergedCoordinates.map((ring) => [ring])
          );

          if (unionResult.length === 1) {
            buildingGeometry = { type: 'Polygon', coordinates: unionResult[0] };
          } else {
            buildingGeometry = { type: 'MultiPolygon', coordinates: unionResult };
          }
        } catch (err) {
          console.warn('polygon-clipping union failed, falling back to multi-polygon', err);
          buildingGeometry = mergedCoordinates.length > 1
            ? { type: 'MultiPolygon', coordinates: mergedCoordinates.map((ring) => [ring]) }
            : { type: 'Polygon', coordinates: mergedCoordinates };
        }

        // Validate geometry to avoid crashing WebGL with malformed data
        const isFiniteNumber = (n: any) => typeof n === 'number' && Number.isFinite(n);
        const isValidLngLat = (lng: any, lat: any) =>
          isFiniteNumber(lng) && isFiniteNumber(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
        const validateCoords = (coords: any): boolean => {
          try {
            // Polygon: number[][][]; MultiPolygon: number[][][][]
            const polygons = buildingGeometry.type === 'Polygon' ? [coords] : coords;
            for (const poly of polygons) {
              for (const ring of poly) {
                for (const pair of ring) {
                  if (!isValidLngLat(pair[0], pair[1])) return false;
                }
              }
            }
            return true;
          } catch { return false; }
        };

        setSelectedBuildingId(buildingId);

        // Update the selected building highlight
        if (mapRef.current) {
          const source = mapRef.current.getSource('selected-building') as mapboxgl.GeoJSONSource;
          try {
            if (buildingGeometry && validateCoords(buildingGeometry.coordinates)) {
              source.setData({
                type: 'FeatureCollection',
                features: [{
                  type: 'Feature',
                  geometry: buildingGeometry,
                  properties: {}
                }]
              });
              // Ensure canvas adjusts after DOM changes
              mapRef.current.resize();
            } else {
              console.warn('Invalid geometry skipped for highlighting');
            }
          } catch (err) {
            console.error('Error updating selected-building source', err);
          }
        }

        // Move marker to centroid of merged coordinates
        if (markerRef.current && mergedCoordinates.length > 0) {
          try {
            let sumLng = 0;
            let sumLat = 0;
            let count = 0;
            mergedCoordinates.forEach((ring) => {
              ring.forEach((coord) => {
                sumLng += coord[0];
                sumLat += coord[1];
                count += 1;
              });
            });
            if (count > 0) {
              const centroid = { lat: sumLat / count, lng: sumLng / count };
              markerRef.current.setLngLat([centroid.lng, centroid.lat]);
            }
          } catch (err) {
            console.error('Error moving marker to merged centroid', err);
          }
        }

        // Notify parent
        onBuildingSelectRef.current(buildingId, buildingGeometry, userClickedCoordinates);
      });

      // Handle click on empty space (deselect building)
      mapRef.current.on('click', (e) => {
        // Check if click was on a building
        const features = mapRef.current?.queryRenderedFeatures(e.point, {
          layers: ['building']
        });

        if (!features || features.length === 0) {
          // Clear selection
          setSelectedBuildingId(null);

          if (mapRef.current) {
            const source = mapRef.current.getSource('selected-building') as mapboxgl.GeoJSONSource;
            source.setData({
              type: 'FeatureCollection',
              features: []
            });
          }

          onBuildingSelectRef.current(null, null, null);
        }
      });
    });

    // Cleanup
    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
      }
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // If the parent provides a new location, recenter without recreating the map
  useEffect(() => {
    if (!mapRef.current || !location) return;
    try {
      mapRef.current.jumpTo({ center: [location.lng, location.lat], zoom: Math.max(mapRef.current.getZoom(), 17) });
      markerRef.current?.setLngLat([location.lng, location.lat]);
    } catch { /* no-op */ }
  }, [location]);

  // Update hover effect
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;

    // Reset any previous hover effects
    mapRef.current.setPaintProperty('building', 'fill-opacity', [
      'case',
      ['==', ['id'], hoveredBuildingId],
      0.8,
      0.5
    ]);
  }, [hoveredBuildingId]);

  if (!isVisible) return null;

  return (
    <div className="building-selection-map-container">
      <div className="building-selection-map-instructions">
        <p>
          <Trans i18nKey="building.offMarket.modal.clickBuildingInstruction" />
        </p>
        {selectedBuildingId && buildingAddress && (
          <p className="building-selection-map-selected">
            ✓ <Trans i18nKey="building.offMarket.modal.buildingSelected" />
          </p>
        )}
        {selectedBuildingId && buildingAddress && (
          <p className="building-selection-map-selected">
            <Trans i18nKey="building.offMarket.modal.receivedAddress" /> 
            <div className="building-selection-map-selected__received-address">
              {buildingAddress}
            </div>
          </p>
        )}
      </div>
      {isQueryingBuilding && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--loading">
          <Trans i18nKey="building.offMarket.modal.checkingBuilding" />
        </div>
      )}
      {!isQueryingBuilding && address && buildingId && !showBuildingSelectionMap && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--found">
          ✓ <Trans i18nKey="building.offMarket.modal.buildingFound" />
        </div>
      )}
      {!isQueryingBuilding && address && isAddressIncomplete && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--not-found">
          <Trans i18nKey="building.offMarket.modal.incompleteAddress" />
        </div>
      )}
      {!isQueryingBuilding && address && !buildingId && location && !isAddressIncomplete && !showBuildingSelectionMap && (
        <div className="could-buy-or-could-sell-modal__building-status could-buy-or-could-sell-modal__building-status--not-found">
          <Trans i18nKey={`building.offMarket.modal.${actionType}InterestNoBuildingFound`} />
        </div>
      )}
      {/* Mismatch warning removed in new flow */}
      <div ref={mapContainerRef} className="building-selection-map" />
    </div>
  );
};

export default BuildingSelectionMap; 